import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faTrashAlt, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { useGetInvoiceAttachments } from "api/queries/invoice/useGetInvoiceAttachments";
import { deleteFile, deleteInvoiceBucketAttachment, uploadFile } from "api";
import errorToast from "helpers/errorToast";
import UploadBar from "components/atoms/UploadBar/UploadBar";
import { invoiceQueryKeys } from "api/queries/invoice/invoiceQueryKeys";
import useConfirm from "hooks/useConfirm";
import { ReactComponent as EditIcon } from "assets/images/iconsV2/edit-02.svg";
import InvoiceFormBucketAttachmentModal from "./InvoiceFormBucketAttachmentModal";
import {
  GroupedBucketAttachment,
  INVOICE_ATTACHMENT_ACCEPT,
  InvoiceFormStop,
  buildGroupedAttachmentLabel,
  groupBucketAttachmentsByFile,
  truncateAttachmentFileName,
} from "./invoiceBucketAttachmentHelpers";
import "./InvoiceFormBucketAttachmentItem.scss";

type InvoiceFormBucketAttachmentItemProps = {
  bucket: any;
  isCustomsInvoice?: boolean;
  isIntermediateInvoice?: boolean;
  missingAttachmentBuckets: string[];
  stopAssociationEnabled?: boolean;
  shipmentStops?: InvoiceFormStop[];
};

const currentInvoiceAttachmentsSelector = (data: any, id: number) =>
  data?.bucket_attachments?.filter((item: any) => item?.attachment_bucket?.id === id) || [];

function InvoiceFormBucketAttachmentItem({
  bucket,
  isCustomsInvoice,
  isIntermediateInvoice,
  missingAttachmentBuckets,
  stopAssociationEnabled = false,
  shipmentStops = [],
}: Readonly<InvoiceFormBucketAttachmentItemProps>) {
  const intl = useIntl();
  const { confirm, ConfirmModal } = useConfirm();
  const { bucket_name, is_mandatory, is_mandatory_on_intermediate_invoice, id } = bucket;
  const attachmentIsMandatory = is_mandatory || (isIntermediateInvoice && is_mandatory_on_intermediate_invoice);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [showModal, setShowModal] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState<GroupedBucketAttachment | null>(null);

  useEffect(() => {
    if (isCustomsInvoice) return;
    setShowRequiredError(missingAttachmentBuckets?.includes(bucket.bucket_name));
  }, [missingAttachmentBuckets, isCustomsInvoice, bucket]);

  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const hiddenFileInput = useRef<any>(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { data: currentInvoiceAttachments }: any = useGetInvoiceAttachments({
    invoiceId: currentInvoiceId!,
    options: { refetchOnMount: false, select: useCallback((data) => currentInvoiceAttachmentsSelector(data, id), [id]) },
  });

  const groupedAttachments = useMemo(
    () => groupBucketAttachmentsByFile(currentInvoiceAttachments, id),
    [currentInvoiceAttachments, id],
  );

  const invalidateAttachments = () => {
    queryClient.invalidateQueries({ queryKey: invoiceQueryKeys.invoiceAttachmentDetails(currentInvoiceId!) });
  };

  const uploadFiles = (file: any) => {
    if (!file) return;
    setUploadInProgress(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("filename", file.name);
    formData.append("invoice_reference_number", currentInvoiceId!);
    formData.append("invoice_attachment_bucket_pk", id);

    uploadFile(formData, {
      onUploadProgress: (progressEvent: any) => {
        setPercentCompleted(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      },
    })
      .then((res) => {
        if (res.status === 201) {
          invalidateAttachments();
          setUploadInProgress(false);
          setPercentCompleted(0);
          setUploadError(false);
          setShowRequiredError(false);
        }
      })
      .catch((error) => {
        errorToast(error);
        setUploadError(true);
        setUploadInProgress(false);
      });
  };

  const handleLegacyDeleteFile = (reference: string) => {
    if (!reference) return;
    setIsLoading(true);
    deleteFile(reference)
      .then((res) => {
        if (res.status === 204) {
          invalidateAttachments();
        } else throw new Error("Could not delete file, please try again later");
        setIsLoading(false);
      })
      .catch((err) => {
        errorToast(err, "Could not delete file, please try again later");
        setIsLoading(false);
      });
  };

  const handleMultistopDeleteFile = async (attachment: GroupedBucketAttachment): Promise<boolean> => {
    const confirmed = await confirm({
      title: intl.formatMessage({
        id: "billing.invoiceAttachment.confirmDeleteTitle",
        defaultMessage: "Delete attachment",
      }),
      message: intl.formatMessage({
        id: "billing.invoiceAttachment.confirmDeleteMessage",
        defaultMessage: "Confirm to delete attachment?",
      }),
    });
    if (!confirmed) return false;

    setIsLoading(true);
    try {
      await deleteInvoiceBucketAttachment(
        currentInvoiceId!,
        attachment.file.reference_number,
        id,
      );
      invalidateAttachments();
      return true;
    } catch (err) {
      errorToast(err, "Could not delete file, please try again later");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    if (stopAssociationEnabled) {
      setModalMode("add");
      setEditingAttachment(null);
      setShowModal(true);
      return;
    }
    hiddenFileInput.current.click();
  };

  const handleChange = (event: any) => {
    const file = event.target.files[0];
    uploadFiles(file);
  };

  const handleEditAttachment = (attachment: GroupedBucketAttachment) => {
    setModalMode("edit");
    setEditingAttachment(attachment);
    setShowModal(true);
  };

  const hasAttachments = stopAssociationEnabled
    ? groupedAttachments.length > 0
    : currentInvoiceAttachments?.length > 0;

  const renderLegacyAttachments = () =>
    currentInvoiceAttachments.map((currentAttachment: any) => (
      <div key={currentAttachment?.file?.id} className="d-flex flex-column align-items-end text-right mb-1">
        <div className="d-flex align-items-center justify-content-end">
          <a
            target="_blank"
            href={currentAttachment?.file?.url}
            rel="noopener noreferrer"
            title={currentAttachment?.file?.name}
          >
            {truncateAttachmentFileName(currentAttachment?.file?.name)}
          </a>
          <FontAwesomeIcon
            icon={faTrashAlt}
            className="ml-2 cursor-pointer text-danger"
            onClick={() => handleLegacyDeleteFile(currentAttachment?.file?.reference_number)}
          />
        </div>
      </div>
    ));

  const renderMultistopAttachments = () =>
    groupedAttachments.map((attachment) => (
      <div key={attachment.file.id} className="d-flex align-items-start justify-content-end mb-1">
        <div className="d-flex flex-column align-items-end text-right">
          <a target="_blank" href={attachment.file.url} rel="noopener noreferrer" title={attachment.file.name}>
            {truncateAttachmentFileName(attachment.file.name)}
          </a>
          <span className="text-secondary text-xxs text-right">
            {buildGroupedAttachmentLabel(attachment, intl)}
          </span>
        </div>
        <div className="d-flex align-items-center ml-2" style={{ height: 20, gap: 8 }}>
          <button
            type="button"
            className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center"
            style={{ width: 16, height: 16, lineHeight: 1 }}
            aria-label={intl.formatMessage({
              id: "billing.invoiceAttachment.modal.editTitle",
              defaultMessage: "Edit attachment",
            })}
            onClick={() => handleEditAttachment(attachment)}
          >
            <EditIcon width={16} height={16} />
          </button>
          <button
            type="button"
            className="btn btn-link p-0 d-inline-flex align-items-center justify-content-center text-danger"
            style={{ width: 16, height: 16, lineHeight: 1 }}
            aria-label={intl.formatMessage({
              id: "billing.invoiceAttachment.confirmDeleteTitle",
              defaultMessage: "Delete attachment",
            })}
            onClick={() => handleMultistopDeleteFile(attachment)}
          >
            <FontAwesomeIcon icon={faTrashAlt} style={{ width: 13, height: 13 }} />
          </button>
        </div>
      </div>
    ));

  const attachLabelId = hasAttachments
    ? "billing.invoiceAttachment.attachAnotherFile"
    : "new-request.attachments.attachmentFile.attachFile";

  const attachLabelDefault = hasAttachments ? "Attach another file" : "Attach file";

  return (
    <>
      <ConfirmModal />
      {stopAssociationEnabled && currentInvoiceId && (
        <InvoiceFormBucketAttachmentModal
          show={showModal}
          mode={modalMode}
          invoiceId={currentInvoiceId}
          bucketId={id}
          shipmentStops={shipmentStops}
          editingAttachment={editingAttachment}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            invalidateAttachments();
            setShowRequiredError(false);
          }}
          onDelete={handleMultistopDeleteFile}
        />
      )}
      <Row className="mb-3 invoice-form-bucket-attachment-row">
        <Col md={3} className={`font-b4 mb-1 no-wrap ${attachmentIsMandatory && !isCustomsInvoice ? "required" : ""}`}>
          {bucket_name}
        </Col>
        <Col md={2}>
          {uploadInProgress && <UploadBar percentCompleted={percentCompleted} />}
          {uploadError && !uploadInProgress && (
            <div className="text-danger d-flex flex-column align-items-center">
              <div>
                <FormattedMessage
                  id="new-request.attachments.attachmentFile.uploadFailed"
                  defaultMessage="Upload failed. Please try again"
                />
              </div>
              <div>
                <FormattedMessage
                  id="new-request.attachments.attachmentFile.maxUploadSize"
                  defaultMessage="(max filesize 15mb)"
                />
              </div>
            </div>
          )}
        </Col>
        <Col md={7} className="d-flex flex-column align-items-end">
          {hasAttachments && !isLoading && (
            <div className="d-flex flex-column w-100 align-items-end">
              {stopAssociationEnabled ? renderMultistopAttachments() : renderLegacyAttachments()}
            </div>
          )}
          {isLoading && (
            <div className="d-flex align-items-center justify-content-end">
              <FontAwesomeIcon icon={faCircleNotch} className="font-16 text-secondary " spin />
            </div>
          )}
          {(!hasAttachments || stopAssociationEnabled) && !isLoading && !uploadInProgress && (
            <div className="d-flex align-items-center justify-content-end">
              <Button type="button" variant="link" name="file" className="pr-0" onClick={handleClick}>
                <div className="flex-row d-flex" style={{ display: "inline-block !important" }}>
                  <FontAwesomeIcon icon={faLink} />
                  <p className="mb-0 no-wrap">
                    <FormattedMessage id={attachLabelId} defaultMessage={attachLabelDefault} />
                    {!stopAssociationEnabled && (
                      <input
                        type="file"
                        accept={INVOICE_ATTACHMENT_ACCEPT}
                        ref={hiddenFileInput}
                        className="hidden-file-input"
                        onChange={handleChange}
                      />
                    )}
                  </p>
                </div>
              </Button>
              {/* Spacer matches edit/delete icon group so the CTA aligns with stop labels */}
              {stopAssociationEnabled && (
                <div className="ml-2" style={{ width: 40, flexShrink: 0 }} aria-hidden />
              )}
            </div>
          )}
        </Col>
      </Row>
      {showRequiredError && !isCustomsInvoice && (
        <div className="text-error-primary mb-lg text-xxs" style={{ marginTop: "-10px" }}>
          <FormattedMessage
            id="new-request.attachments.attachment.attachmentIsRequired"
            defaultMessage="This attachment is required"
          />
        </div>
      )}
    </>
  );
}

InvoiceFormBucketAttachmentItem.defaultProps = {
  isCustomsInvoice: false,
  isIntermediateInvoice: false,
  stopAssociationEnabled: false,
  shipmentStops: [],
};

export default InvoiceFormBucketAttachmentItem;
